/**
 * types.ts
 *
 * Wire-format types for the BizFirst Communication API's notification surface.
 *
 * Verified against (BizFirstPayrollV3 repo):
 * - src/mvc-server/Go/Communication/BizFirstFi.Go.Communication.Api.Base/Controllers/BaseNotificationController.cs
 * - src/mvc-server/Go/Communication/BizFirstFi.Go.Communication.Domain/WebRequests/Notification/NotificationSearchRequests.cs
 * - src/mvc-server/Go/Communication/BizFirstFi.Go.Communication.Domain/Entities/Notification.cs
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Request/BaseRequest/GoWebRequest.cs
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Request/Search/Base/Super/SuperBaseSearchRequest.cs
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Request/Search/Standard/StandardSearchRequest.cs
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Pages/PageInfo.cs
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Errors/GoWebError.cs
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/IdInfo/IDInfo.cs (+ IDInfoJsonConverter.cs)
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Metadata/GoWebRequestMetadata.cs
 * - src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Request/ByIdGet/ByIdGetWebRequest.cs
 *
 * JSON CASING ASSUMPTION: BizFirstFi.Go.Communication.Api's Program.cs calls only
 * `builder.Services.AddControllers()` with no `AddJsonOptions`/`JsonNamingPolicy`
 * override, so ASP.NET Core's default `JsonSerializerDefaults.Web` applies, which
 * camel-cases property names (e.g. C# `NotificationID` -> JSON `notificationID`,
 * `UserID` -> `userID`). This was confirmed by reading Program.cs but NOT verified
 * against a live HTTP response (no backend is reachable from this environment) — if
 * a future change adds an explicit naming policy, the field names below (and the
 * runtime property access in api/notificationApiClient.ts) will need updating.
 *
 * EXCEPTION to the casing rule: `IDInfo` has a custom `[JsonConverter(typeof(IDInfoJsonConverter))]`
 * that explicitly writes `{ "id": <number>, "resID": <string> }` regardless of the
 * naming policy (IDInfoJsonConverter.Write hardcodes those literal property names) —
 * so `IDInfo` below is camelCase-first ("id") but keeps "resID" capitalized, which is
 * NOT what the generic camelCase rule would produce for "ResID" (would be "resID"
 * either way here, but note this field is converter-driven, not policy-driven).
 */

/** Mirrors C# `IDInfo` exactly as written by `IDInfoJsonConverter.Write` (id, resID). */
export interface IDInfo {
  id: number
  resID: string
}

/** Convenience builders for IDInfo — mirrors IDInfo.Create()/eIdType semantics. */
export function idInfoFromId(id: number): IDInfo {
  return { id, resID: '' }
}

export function idInfoFromResId(resID: string): IDInfo {
  return { id: 0, resID }
}

/**
 * A single message/warning/info entry as returned in a response's `errors` array
 * (C# `GoWebError`, serialized as a plain array — the C# `GoWebErrors` type is just
 * `List<GoWebError>`, not a nested/keyed object).
 */
export interface NotificationApiErrorEntry {
  code?: string
  message: string
  field?: string | null
  severity?: 'Error' | 'Warning' | 'Info' | string
  details?: Record<string, unknown>
}

/** Mirrors C# `GoIsolationMetadata`. */
export interface NotificationRequestIsolationMetadata {
  appID?: number | null
  tenantID?: number | null
  userID?: number | null
}

/**
 * Mirrors C# `GoWebRequestMetadata`. Every field has a server-side default
 * (`GoWebRequest.Metadata` is initialized to `new GoWebRequestMetadata()` and the
 * ASP.NET model binder only overwrites properties actually present in the request
 * body), so this entire object — and every field on it — is optional from the
 * client's point of view. Callers of this package never need to construct one.
 */
export interface NotificationRequestMetadata {
  headers?: Record<string, string>
  requestId?: string
  correlationId?: string
  timestamp?: string
  version?: string
  isolation?: NotificationRequestIsolationMetadata | null
  additional?: Record<string, unknown>
}

/** Mirrors C# `GoPageInfo` — the request-side pagination/sort block. */
export interface NotificationPageInfoRequest {
  pageNumber?: number
  pageSize?: number
  sortBy?: string | null
  sortOrder?: 'asc' | 'desc' | string
}

/**
 * Mirrors C# `GoPageInfoFull` — the response-side pagination block. `totalRecords`
 * (C# `TotalRecords`) is the field this package reads for `unreadCount`: confirmed
 * by reading `GoPageInfoFull` in
 * BizFirstPayrollV3/src/mvc-server/Go/Essentials/BizFirstFi.Go.Essentials.Domain/Request/Pages/PageInfo.cs
 * and tracing `search/unread`'s response type back through
 * `BaseNotificationController.GetUnread` -> `WebResponse_Search` ->
 * `GoWebStandardSearchResponse` -> `SuperGoWebBaseSearchResponse.PageInfo` (that
 * exact chain is how the `search/*` routes get their `pageInfo`, as opposed to the
 * differently-shaped `GoWebSearchResponse<T>` defined in GoWebRequest.cs, which no
 * notification route actually uses).
 */
export interface NotificationPageInfo {
  pageNumber: number
  pageSize: number
  sortBy: string | null
  sortOrder: string
  totalPages: number
  totalRecords: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

/**
 * The subset of C# `Notification` (Comm_Notifications table) relevant to a header
 * bell/dropdown display. Deliberately excludes delivery-provider internals
 * (EmailProvider/EmailMessageID, SMSProvider/SMSMessageID, PushProvider/
 * PushMessageID, TemplateID/TemplateVersion/TemplateData, AttemptCount/MaxAttempts/
 * LastAttemptAt/FailureReason, RecipientEmail/RecipientPhone/RecipientDeviceTokens,
 * DisplayOrder, the *ID lookup foreign keys, SubmissionID, DeliveryMethod, Language,
 * ResponseExpected/ResponseReceived/ResponseData/ResponseAt) — a display client has
 * no business coupling to those backend delivery mechanics (SRP).
 */
export interface NotificationItem {
  notificationID: number
  userID: number
  title: string
  message: string
  type: string
  priority: string
  /** e.g. "Pending" | "Sent" | "Delivered" | "Failed" | "Read". Not itself the
   *  authoritative unread signal — see `readAt` / `isNotificationUnread()` below. */
  status: string
  actionRequired: boolean
  actionUrl?: string | null
  actionButtonText?: string | null
  scheduledAt?: string | null
  sentAt?: string | null
  deliveredAt?: string | null
  /** `null`/`undefined` means unread. This is the real unread signal per the
   *  Notification entity's own doc comment — not `status`. */
  readAt?: string | null
}

/** `ReadAt == null` is the authoritative "unread" signal (per Notification.cs's own
 *  doc comments), not `status`. Exported so consumers don't have to re-derive it. */
export function isNotificationUnread(item: NotificationItem): boolean {
  return item.readAt === null || item.readAt === undefined
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

/** Body for `POST api/v1/communication/notification/search/by-user`
 *  (C# `GetByUserSearchRequest : GoWebStandardSearchRequest`). */
export interface GetNotificationsByUserRequest {
  userID: IDInfo
  pageInfo?: NotificationPageInfoRequest
  filters?: Record<string, unknown>
  metadata?: NotificationRequestMetadata
}

/** Body for `POST api/v1/communication/notification/search/unread`
 *  (C# `GetUnreadNotificationsSearchRequest : GoWebStandardSearchRequest` — no
 *  extra fields beyond the base search request; the current user is inferred
 *  server-side from the bearer token). */
export interface GetUnreadNotificationsRequest {
  pageInfo?: NotificationPageInfoRequest
  filters?: Record<string, unknown>
  metadata?: NotificationRequestMetadata
}

/** Body for `POST api/v1/communication/notification/get`
 *  (C# `GetByIdWebRequest : GoWebRequest`). */
export interface GetNotificationByIdRequest {
  id: IDInfo
  raiseErrorIfNotFound?: boolean
  metadata?: NotificationRequestMetadata
}

// ---------------------------------------------------------------------------
// Response envelopes (as received on the wire)
// ---------------------------------------------------------------------------

/** Common envelope fields present on every GoWebResponse-derived response. */
export interface NotificationApiEnvelope {
  success: boolean
  errors: NotificationApiErrorEntry[]
  metadata?: NotificationRequestMetadata
}

/** Wire shape of `search/by-user` and `search/unread` responses
 *  (C# `GoWebStandardSearchResponse<Notification>`, via `WebResponse_Search`). */
export interface NotificationSearchResponseEnvelope extends NotificationApiEnvelope {
  data: NotificationItem[] | null
  pageInfo: NotificationPageInfo
}

/** Wire shape of the `get` (by-id) response (C# `GetByIdWebResponse<Notification>`,
 *  via `WebResponse_GetById`). */
export interface NotificationGetByIdResponseEnvelope extends NotificationApiEnvelope {
  data: NotificationItem | null
}

// ---------------------------------------------------------------------------
// Client-facing result shapes (post-unwrap)
// ---------------------------------------------------------------------------

/** Result of a search call, after the transport layer has unwrapped the envelope
 *  and validated `success`. */
export interface NotificationSearchResult {
  items: NotificationItem[]
  pageInfo: NotificationPageInfo
}
